import {autoserializeAs, autoserializeUsing, inheritSerialization} from "dcerialize";
import type {DateTime} from "luxon";
import {DateTimeSerializer} from "../../helpers/DateSerializers";
import type {EnumTranslations} from "../../helpers/EnumHelpers";
import {nonEmpty} from "../../helpers/OptionalStringDecorators";
import {IdOnlyBactoforceLocation, SimpleBactoforceLocation} from "../bactoforce/BactoforceLocation";
import {createExisting, createNew, type Existing, type IdentifiableModel, type New} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import {CodeOnlyCountry, SimpleCountry} from "../i18n/Country";
import {CodeOnlyLanguage, SimpleLanguage} from "../i18n/Language";
import {ModificationDetails} from "../ModificationDetails";
import {IdOnlyPriceList, SimplePriceList} from "../pricing/PriceList";
import {IdOnlyUser, SimpleUser} from "../users/User";
import {IdOnlyCustomerOrganization, SimpleCustomerOrganization} from "./CustomerOrganization";
import {IdOnlyIndustrySection, SimpleIndustrySection} from "./IndustrySection";

export enum CustomerLocationSize {
  Small = 1,
  Medium = 2,
  Large = 3,
}

export const customerLocationSizeTranslations: EnumTranslations<CustomerLocationSize> = {
  get [CustomerLocationSize.Small](): string {
    return $localize`:@@customer-location-size.small:Small`;
  },
  get [CustomerLocationSize.Medium](): string {
    return $localize`:@@customer-location-size.medium:Medium`;
  },
  get [CustomerLocationSize.Large](): string {
    return $localize`:@@customer-location-size.large:Large`;
  },
};

export enum CustomerLocationClassification {
  A = "A",
  B = "B",
  C = "C",
}

export const customerLocationClassificationTranslations: EnumTranslations<CustomerLocationClassification> = {
  [CustomerLocationClassification.A]: "A",
  [CustomerLocationClassification.B]: "B",
  [CustomerLocationClassification.C]: "C",
};

export enum CustomerLocationAgreementExpirationMode {
  None = "N",
  OnDate = "D",
  AutomaticRenewal = "A",
}

export const customerLocationAgreementExpirationModeTranslations: EnumTranslations<CustomerLocationAgreementExpirationMode> = {
  get [CustomerLocationAgreementExpirationMode.None](): string {
    return $localize`:@@customer-location-agreement-expiration-mode.none:No expiration`;
  },
  get [CustomerLocationAgreementExpirationMode.OnDate](): string {
    return $localize`:@@customer-location-agreement-expiration-mode.on-date:Expire on date`;
  },
  get [CustomerLocationAgreementExpirationMode.AutomaticRenewal](): string {
    return $localize`:@@customer-location-agreement-expiration-mode.automatic-renewal:Automatic renewal`;
  },
};

export enum CustomerLocationCompetitionStatus {
  Unknown = "U",
  None = "N",
  Competitor = "C",
  InhouseInspections = "I",
}

export const customerLocationCompetitionStatusTranslations: EnumTranslations<CustomerLocationCompetitionStatus> = {
  get [CustomerLocationCompetitionStatus.Unknown](): string {
    return $localize`:@@customer-location-competitor-status.unknown:Unknown`;
  },
  get [CustomerLocationCompetitionStatus.None](): string {
    return $localize`:@@customer-location-competitor-status.none:None`;
  },
  get [CustomerLocationCompetitionStatus.Competitor](): string {
    return $localize`:@@customer-location-competitor-status.competitor:Competitor`;
  },
  get [CustomerLocationCompetitionStatus.InhouseInspections](): string {
    return $localize`:@@customer-location-competitor-status.inhouse-inspections:In-house inspections`;
  },
};

export enum CustomerLocationSalesPriority {
  Low = 1,
  Medium = 2,
  High = 3,
  FocusCustomer = 4,
}

export const customerLocationSalesPriorityTranslations: EnumTranslations<CustomerLocationSalesPriority> = {
  get [CustomerLocationSalesPriority.Low](): string {
    return $localize`:@@customer-location-sales-priority.low:Low`;
  },
  get [CustomerLocationSalesPriority.Medium](): string {
    return $localize`:@@customer-location-sales-priority.medium:Medium`;
  },
  get [CustomerLocationSalesPriority.High](): string {
    return $localize`:@@customer-location-sales-priority.high:High`;
  },
  get [CustomerLocationSalesPriority.FocusCustomer](): string {
    return $localize`:@@customer-location-sales-priority.focus-customer:Focus Customer`;
  },
};

export enum CustomerLocationStatus {
  Inactive = "I",
  NonRecurring = "N",
  Recurring = "R",
}

export const customerLocationStatusTranslations: EnumTranslations<CustomerLocationStatus> = {
  get [CustomerLocationStatus.Inactive](): string {
    return $localize`:@@customer-location-status.inactive:Inactive`;
  },
  get [CustomerLocationStatus.NonRecurring](): string {
    return $localize`:@@customer-location-status.non-recurring:Non-Recurring`;
  },
  get [CustomerLocationStatus.Recurring](): string {
    return $localize`:@@customer-location-status.recurring:Recurring`;
  },
};

export class IdOnlyCustomerLocation implements IdentifiableModel {
  @autoserializeAs(() => Number)
  public id?: number;

  public static existing(id: number): Existing<IdOnlyCustomerLocation> {
    return createExisting(IdOnlyCustomerLocation, id);
  }
}

@inheritSerialization(() => IdOnlyCustomerLocation)
export class NameOnlyCustomerLocation extends IdOnlyCustomerLocation {
  @autoserializeAs(() => String)
  public name: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public number?: string;
  @autoserializeAs(() => Boolean)
  public isActive: boolean;
  @autoserializeAs(() => Boolean)
  public readonly isDeleted: boolean;
}

@inheritSerialization(() => NameOnlyCustomerLocation)
export class DashboardCustomerLocation extends NameOnlyCustomerLocation {
  @autoserializeAs(() => SimpleCustomerOrganization)
  public readonly organization: Readonly<Existing<SimpleCustomerOrganization>>;
}

/**
 * @sealed
 */
@inheritSerialization(() => DashboardCustomerLocation)
export class SimpleCustomerLocation extends DashboardCustomerLocation {
  @autoserializeAs(() => SimpleBactoforceLocation)
  public readonly bactoforceLocation: Readonly<Existing<SimpleBactoforceLocation>>;
}

@inheritSerialization(() => NameOnlyCustomerLocation)
export class WritableCustomerLocation extends NameOnlyCustomerLocation {
  @autoserializeAs(() => IdOnlyCustomerOrganization)
  public organization: Existing<IdOnlyCustomerOrganization>;
  @autoserializeAs(() => String)
  @nonEmpty
  public email?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public phone?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public fax?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public street?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public zip?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public city?: string;
  @autoserializeAs(() => Boolean)
  public invoiceCostsByDefault: boolean;
  @autoserializeAs(() => Number)
  public size?: CustomerLocationSize;
  @autoserializeAs(() => String)
  public classification?: CustomerLocationClassification;
  @autoserializeAs(() => String)
  @nonEmpty
  public agreementNumber?: string;
  @autoserializeAs(() => String)
  public agreementExpirationMode?: CustomerLocationAgreementExpirationMode;
  @autoserializeUsing(DateTimeSerializer)
  public agreementExpiresAt?: DateTime;
  @autoserializeAs(() => String)
  @nonEmpty
  public productDescription?: string;
  @autoserializeAs(() => String)
  public competitionStatus?: CustomerLocationCompetitionStatus;
  @autoserializeAs(() => String)
  @nonEmpty
  public competitorInformation?: string;
  @autoserializeAs(() => Number)
  public salesPriority?: CustomerLocationSalesPriority;
  @autoserializeAs(() => String)
  @nonEmpty
  public generalNotes?: string;
  @autoserializeAs(() => CodeOnlyLanguage)
  public language: CodeOnlyLanguage;
  @autoserializeAs(() => CodeOnlyCountry)
  public country: CodeOnlyCountry;
  @autoserializeAs(() => IdOnlyUser)
  public responsibleBactoforceUser?: Existing<IdOnlyUser>;
  @autoserializeAs(() => IdOnlyUser)
  public mainContactPerson?: Existing<IdOnlyUser>;
  @autoserializeAs(() => IdOnlyBactoforceLocation)
  public bactoforceLocation: Existing<IdOnlyBactoforceLocation>;
  @autoserializeAs(() => IdOnlyIndustrySection)
  public industrySection: Existing<IdOnlyIndustrySection>;
  @autoserializeAs(() => IdOnlyPriceList)
  public priceList: Existing<IdOnlyPriceList>;

  public static createNew(organization: Readonly<Existing<IdOnlyCustomerOrganization>>,
                          name: string,
                          language: Readonly<CodeOnlyLanguage>,
                          country: Readonly<CodeOnlyCountry>,
                          bactoforceLocation: Readonly<Existing<IdOnlyBactoforceLocation>>,
                          industrySection: Readonly<Existing<IdOnlyIndustrySection>>,
                          priceList: Readonly<Existing<IdOnlyPriceList>>,
                          invoiceCostsByDefault: boolean = false): New<WritableCustomerLocation> {
    const customerLocation = createNew(WritableCustomerLocation);
    customerLocation.name = name;
    customerLocation.organization = organization;
    customerLocation.language = language;
    customerLocation.country = country;
    customerLocation.bactoforceLocation = bactoforceLocation;
    customerLocation.industrySection = industrySection;
    customerLocation.priceList = priceList;
    customerLocation.invoiceCostsByDefault = invoiceCostsByDefault;
    customerLocation.isActive = true;
    return Object.assign(customerLocation, {isDeleted: false});
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableCustomerLocation)
export class CustomerLocation extends WritableCustomerLocation implements ModificationTrackedModel {
  @autoserializeAs(() => SimpleCustomerOrganization)
  public override organization: Readonly<Existing<SimpleCustomerOrganization>>;
  @autoserializeAs(() => SimpleLanguage)
  public override language: Readonly<SimpleLanguage>;
  @autoserializeAs(() => SimpleCountry)
  public override country: Readonly<SimpleCountry>;
  @autoserializeAs(() => SimpleUser)
  public override responsibleBactoforceUser?: Readonly<Existing<SimpleUser>>;
  @autoserializeAs(() => SimpleUser)
  public override mainContactPerson?: Readonly<Existing<SimpleUser>>;
  @autoserializeAs(() => SimpleBactoforceLocation)
  public override bactoforceLocation: Readonly<Existing<SimpleBactoforceLocation>>;
  @autoserializeAs(() => SimpleIndustrySection)
  public override industrySection: Readonly<Existing<SimpleIndustrySection>>;
  @autoserializeAs(() => SimplePriceList)
  public override priceList: Readonly<Existing<SimplePriceList>>;

  @autoserializeAs(() => String)
  public readonly status: CustomerLocationStatus;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;

  public get translatedStatus(): string {
    return customerLocationStatusTranslations[this.status];
  }
}
